import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import * as Yup from "yup";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import "dayjs/locale/es";
import { notification } from "../../utils/Notifications.tsx";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import S3Service from "../../services/s3.service.ts";
import {
  getHolidayById,
  approveHoliday,
  getHolidaysByPartner,
} from "../../services/holidays.service.ts";
import { getScheduleByPartner } from "../../services/branch-schedule.service.ts";
import HolidayFormEditFiles from "../../components/holidays/HolidayFormEditFiles.tsx";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { Panel, PanelHeader } from "../../components/panel/panel.jsx";
import Button from "../../components/Button.tsx";
import Loader from "../../components/Loader.tsx";
import moment from "moment";
import "moment/locale/es";
import { DatePicker, ConfigProvider } from "antd";
import esES from "antd/es/locale/es_ES";

// Configurar dayjs
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.locale("es");
moment.locale("es");

const ApprovedHoliday = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  useAppSettings();
  const [loading, setLoading] = useState(false);
  const [holiday, setHoliday] = useState(null);
  const [reloadFiles, setReloadFiles] = useState(false);
  const [profileUrl, setProfileUrl] = useState("");
  const [partnerSchedule, setPartnerSchedule] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [partnerHistory, setPartnerHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const { partner } = useSelector((state: RootState) => state.auth);

  const validationSchema = Yup.object().shape({
    approved: Yup.boolean().required("Debe seleccionar una opción"),
    reviewerComment: Yup.string().when("approved", {
      is: false,
      then: (schema) =>
        schema.required("El comentario es obligatorio al rechazar"),
      otherwise: (schema) => schema.optional(),
    }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      description: "",
      approved: false,
      daysRequested: 1,
      startDate: "",
      endDate: "",
      requireTacticRelease: false,
      reviewerComment: "",
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const holidayData = {
          description: values.description,
          approved: values.approved,
          daysRequested: values.daysRequested,
          startDate: values.startDate
            ? dayjs(values.startDate).unix()
            : undefined,
          endDate: values.endDate ? dayjs(values.endDate).unix() : undefined,
          isRejected: !values.approved,
          requireTacticRelease: values.requireTacticRelease,
          reviewerComment: values.reviewerComment,
        };

        console.log("Enviando datos de vacaciones:", holidayData);

        // Aprobar las vacaciones (el backend se encargará de eliminar horarios si requireTacticRelease es true)
        await approveHoliday(Number(id), holidayData);

        notification(
          "Éxito",
          `Vacaciones ${
            values.approved ? "aprobadas" : "rechazadas"
          } correctamente`,
          "success"
        );
        navigate(-1);
      } catch (error) {
        console.error("Error al procesar las vacaciones:", error);
        notification("Error", "No se pudo procesar las vacaciones", "danger");
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchHoliday = async () => {
    setLoading(true);
    try {
      const response = await getHolidayById(Number(id));
      setHoliday(response);

      if (response.partner?.person?.curp) {
        try {
          const profileUrlResponse = await S3Service.getFile(
            "PERFIL_" + response.partner.person.curp
          );
          setProfileUrl(profileUrlResponse.url);
        } catch (error) {
          console.error("Error al obtener foto de perfil:", error);
          setProfileUrl("/assets/img/avatar.jpg");
        }
      }

      formik.setValues({
        description: response.description || "",
        approved: response.approved || false,
        daysRequested: response.daysRequested || 1,
        startDate: response.startDate
          ? dayjs.unix(response.startDate).format("YYYY-MM-DD")
          : "",
        endDate: response.endDate
          ? dayjs.unix(response.endDate).format("YYYY-MM-DD")
          : "",
        requireTacticRelease: response.requireTacticRelease || false,
        reviewerComment: response.reviewerComment || "",
      });
    } catch (error) {
      console.error("Error al cargar las vacaciones:", error);
      notification("Error", "No se pudo cargar las vacaciones", "danger");
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerSchedule = async () => {
    if (!holiday?.partner?.id || !holiday?.startDate || !holiday?.endDate)
      return;

    setLoadingSchedule(true);
    try {
      const startDate = dayjs.unix(holiday.startDate);
      const endDate = dayjs.unix(holiday.endDate);

      const allPartnerSchedules = await getScheduleByPartner(
        holiday.partner.id
      );

      const schedulesInRange =
        allPartnerSchedules.records?.filter((schedule) => {
          if (!schedule.exactDate) return false;

          const scheduleDate = dayjs(schedule.exactDate);
          const isInRange =
            scheduleDate.isSameOrAfter(startDate, "day") &&
            scheduleDate.isSameOrBefore(endDate, "day");

          return isInRange;
        }) || [];

      setPartnerSchedule(schedulesInRange);
    } catch (error) {
      console.error("Error al cargar el horario del partner:", error);
      setPartnerSchedule(null);
    } finally {
      setLoadingSchedule(false);
    }
  };

  const loadPartnerHistory = async (partnerId: number) => {
    try {
      setLoadingHistory(true);
      const history = await getHolidaysByPartner(partnerId);
      // Ordenar por fecha de creación, más reciente arriba
      const sortedHistory = (history || []).sort((a, b) => {
        const dateA = a.createdAt ? dayjs.unix(a.createdAt) : dayjs(0);
        const dateB = b.createdAt ? dayjs.unix(b.createdAt) : dayjs(0);
        return dateB.isAfter(dateA) ? 1 : -1;
      });
      setPartnerHistory(sortedHistory);
    } catch (error) {
      console.error("Error loading partner history:", error);
      setPartnerHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchHoliday();
  }, [id]);

  useEffect(() => {
    if (holiday) {
      fetchPartnerSchedule();
      if (holiday.partner?.id) {
        loadPartnerHistory(holiday.partner.id);
      }
    }
  }, [holiday]);

  const handleReloadFiles = () => {
    setReloadFiles(!reloadFiles);
    fetchHoliday();
  };

  const handleApproveAsResponsible = async () => {
    setLoading(true);
    try {
      const holidayData = {
        approvedByVacationsResponsible: true,
      };

      console.log("Aprobando como responsable de vacaciones:", holidayData);

      await approveHoliday(Number(id), holidayData);

      notification(
        "Éxito",
        "Vacaciones aprobadas como responsable correctamente",
        "success"
      );
      navigate(-1);
    } catch (error) {
      console.error("Error al aprobar como responsable:", error);
      notification("Error", "No se pudo aprobar como responsable", "danger");
    } finally {
      setLoading(false);
    }
  };

  const getHolidayStatus = () => {
    if (!holiday) return "";
    if (holiday.approved && holiday.approvedByVacationsResponsible) return "APROBADO";
    if (holiday.approved && !holiday.approvedByVacationsResponsible) return "APROBADO POR JEFE";
    if (holiday.isRejected) return "RECHAZADO";
    return "PENDIENTE";
  };

  const getStatusColor = () => {
    if (!holiday) return "bg-secondary";
    if (holiday.approved && holiday.approvedByVacationsResponsible) return "bg-success";
    if (holiday.approved && !holiday.approvedByVacationsResponsible) return "bg-info";
    if (holiday.isRejected) return "bg-danger";
    return "bg-warning";
  };

  const getHistoryStatusColor = (holidayItem: any) => {
    if (holidayItem.approved) return "bg-success";
    if (holidayItem.isRejected) return "bg-danger";
    return "bg-warning";
  };

  const getHistoryStatus = (holidayItem: any) => {
    if (holidayItem.approved) return "APROBADO";
    if (holidayItem.isRejected) return "RECHAZADO";
    return "PENDIENTE";
  };

  const isAlreadyProcessed =
    holiday && (holiday.approved || holiday.isRejected);

  // Verificar si el usuario es responsable de vacaciones
  const isVacationsResponsible = partner?.isVacationsResponsible === true;
  
  // Verificar si esta vacación puede ser aprobada por el responsable
  const canApproveAsResponsible = isVacationsResponsible && 
    holiday?.approved === true && 
    holiday?.approvedByVacationsResponsible === false && 
    holiday?.isRejected === false;

  const formatHolidayDate = (date: string) => {
    if (!date) return "-";
    return moment(date).format("dddd, D [de] MMMM [de] YYYY");
  };

  const formatDateRange = (startDate: number, endDate: number): string => {
    if (!startDate || !endDate) return "-";
    const start = moment.unix(startDate).format("DD/MM/YYYY");
    const end = moment.unix(endDate).format("DD/MM/YYYY");
    return `${start} - ${end}`;
  };

  const formatScheduleTime = (time: string) => {
    if (!time) return "-";
    return time.split(" - ")[0] || time;
  };

  const formatScheduleDate = (date: string) => {
    if (!date) return "-";
    return moment(date).format("dddd, D [de] MMMM [de] YYYY");
  };

  if (!holiday) {
    return (
      <Layout>
        <div>No se encontró la solicitud de vacaciones</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Title
        baseTitle="Revisar vacaciones"
        basePath={`/colaboradores/aprobar-vacaciones/${id}`}
        title="Revisar y procesar vacaciones"
      />

      <div className="card border-0 m-4 rounded">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Estado de las Vacaciones</h5>
            <span className={`badge ${getStatusColor()} fs-6 px-3 py-2`}>
              {getHolidayStatus()}
            </span>
          </div>
          {isAlreadyProcessed && (
            <div className="alert alert-info mt-3 mb-0">
              <i className="fas fa-info-circle me-2"></i>
              Estas vacaciones ya han sido procesadas y no pueden ser
              modificadas.
            </div>
          )}
        </div>
      </div>

      <div className="d-flex flex-column flex-lg-row gap-4 m-4">
        <div className="flex-lg-grow-0" style={{ width: "30%" }}>
          {holiday?.partner && (
            <div className="card border-0 rounded h-100 shadow-sm">
              <div className="card-body">
                {/* Tabs */}
                <div className="d-flex mb-3">
                  <button
                    className={`btn btn-sm flex-fill me-2 ${activeTab === 'info' ? 'btn-azul' : 'btn-outline-azul'}`}
                    onClick={() => setActiveTab('info')}
                  >
                    <i className="fas fa-user me-1"></i>
                    Información
                  </button>
                  <button
                    className={`btn btn-sm flex-fill ${activeTab === 'history' ? 'btn-azul' : 'btn-outline-azul'}`}
                    onClick={() => setActiveTab('history')}
                  >
                    <i className="fas fa-history me-1"></i>
                    Historial
                  </button>
                </div>

                {/* Tab Content */}
                {activeTab === 'info' && (
                  <div className="text-center">
                    <h5 className="mb-4 text-azul">Información del Chilito</h5>

                <div className="mb-4">
                  <img
                    src={profileUrl || "/assets/img/avatar.jpg"}
                    alt="Foto de perfil"
                    className="rounded-circle shadow"
                    style={{
                      width: "120px",
                      height: "120px",
                      objectFit: "cover",
                      border: "4px solid #f8f9fa",
                    }}
                    onError={(e) => {
                      e.target.src = "/assets/img/avatar.jpg";
                    }}
                  />
                </div>

                <div className="text-center">
                  <h4 className="mb-2 fw-bold text-dark">
                    {toCamelCase(
                      `${holiday.partner.person?.firstName} ${holiday.partner.person?.lastName} ${holiday.partner.person?.secondLastName}`
                    )}
                  </h4>

                  <div className="mb-4">
                    <div className="text-muted small mb-1">
                      <i className="fas fa-briefcase me-2"></i>
                      Puesto
                    </div>
                    <div className="fw-semibold fs-6 text-dark">
                      {holiday.partner.candidate?.vacancy?.position?.name}
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="text-muted small mb-1">
                      <i className="fas fa-building me-2"></i>
                      Sucursal
                    </div>
                    <div className="fw-semibold fs-6 text-dark">
                      {holiday.partner.candidate?.branch?.name}
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-top">
                    <h6 className="text-center mb-3">
                      <i className="fas fa-calendar-alt me-2 text-success"></i>
                      Período de Vacaciones
                    </h6>
                    <div className="alert alert-success mb-0 small">
                      <i className="fas fa-check-circle me-2"></i>
                      <strong>Vacaciones solicitadas:</strong>{" "}
                      {holiday.daysRequested || 0} días
                    </div>
                    <div className="alert alert-info mt-2 mb-0 small">
                      <i className="fas fa-info-circle me-2"></i>
                      <strong>Período:</strong>{" "}
                      {formatDateRange(holiday.startDate, holiday.endDate)}
                    </div>

                    <div className="mt-4 pt-3 border-top">
                      <h6 className="text-center mb-3">
                        <i className="fas fa-clock me-2 text-warning"></i>
                        Horarios durante el período
                      </h6>

                      {loadingSchedule ? (
                        <div className="text-center">
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">
                              Cargando horarios...
                            </span>
                          </div>
                          <div className="text-muted small mt-2">
                            Verificando horarios...
                          </div>
                        </div>
                      ) : partnerSchedule && partnerSchedule.length > 0 ? (
                        <div>
                          <div className="alert alert-warning mb-4 small">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            <strong>Atención:</strong> El colaborador tiene{" "}
                            {partnerSchedule.length} horario
                            {partnerSchedule.length > 1 ? "s" : ""} asignado
                            {partnerSchedule.length > 1 ? "s" : ""} durante este
                            período de vacaciones.
                          </div>

                          <div
                            className="schedules-scroll-container"
                            style={{
                              maxHeight: "230px",
                              overflowY: "auto",
                              border: "1px solid #e9ecef",
                              borderRadius: "8px",
                              backgroundColor: "#f8f9fa",
                            }}
                          >
                            <div className="row g-3 p-3">
                              {partnerSchedule.map((schedule, index) => (
                                <div key={index} className="col-12">
                                  <div className="card border-0 bg-white rounded-3 shadow-sm border-start border-3 border-azul">
                                    <div className="card-body p-4">
                                      <div className="row align-items-center">
                                        <div className="col-md-3">
                                          <div className="text-center text-md-start">
                                            <div className="fw-bold text-dark mb-1">
                                              {schedule.positionName ||
                                                "Sin posición"}
                                            </div>
                                            <div className="text-muted small">
                                              {schedule.branch?.name ||
                                                "Sin sucursal"}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="col-md-3">
                                          <div className="text-center">
                                            <div className="fw-semibold text-azul mb-1">
                                              Fecha
                                            </div>
                                            <div className="text-dark">
                                              {formatScheduleDate(
                                                schedule.exactDate
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="col-md-3">
                                          <div className="text-center">
                                            <div className="fw-semibold text-azul mb-1">
                                              Horario
                                            </div>
                                            <div className="text-dark">
                                              {formatScheduleTime(
                                                schedule.startTime
                                              )}{" "}
                                              -{" "}
                                              {formatScheduleTime(
                                                schedule.endTime
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="col-md-3">
                                          <div className="text-center text-md-end">
                                            <div className="fw-semibold text-secondary mb-1">
                                              Turno
                                            </div>
                                            <div className="text-dark">
                                              {schedule.shift || "Sin turno"}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="alert alert-success mb-0 small">
                          <i className="fas fa-check-circle me-2"></i>
                          <strong>Sin conflictos:</strong> El colaborador no
                          tiene horarios asignados durante este período.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
                )}

                {/* Historial Tab */}
                {activeTab === 'history' && (
                  <div>
                    <h5 className="mb-3 text-azul">Historial de vacaciones</h5>
                    {loadingHistory ? (
                      <div className="d-flex flex-column align-items-center py-3">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Cargando...</span>
                        </div>
                        <div className="text-muted small mt-2">Cargando historial...</div>
                      </div>
                    ) : partnerHistory.length > 0 ? (
                      <div className="overflow-auto" style={{ maxHeight: '400px' }}>
                        {partnerHistory.map((item: any, index: number) => (
                          <div key={item.id || index} className="card mb-2 border-light shadow-sm">
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="fw-semibold text-dark">
                                  {item.description ? (item.description.length > 30 ? item.description.substring(0, 30) + '...' : item.description) : 'Sin descripción'}
                                </div>
                                <span className={`badge ${getHistoryStatusColor(item)}`}>
                                  {getHistoryStatus(item)}
                                </span>
                              </div>
                              <div className="text-muted small mb-1">
                                <i className="fas fa-calendar me-1"></i>
                                {item.startDate && item.endDate ? formatDateRange(item.startDate, item.endDate) : '-'}
                              </div>
                              <div className="text-muted small mb-1">
                                <i className="fas fa-clock me-1"></i>
                                {item.daysRequested || 1} día{item.daysRequested > 1 ? 's' : ''}
                              </div>
                              {item.reviewerComment && (
                                <div className="text-muted small">
                                  <i className="fas fa-comment me-1"></i>
                                  {item.reviewerComment.length > 40 ? item.reviewerComment.substring(0, 40) + '...' : item.reviewerComment}
                                </div>
                              )}
                              {item.id === holiday.id && (
                                <div className="mt-2">
                                  <span className="badge bg-info">Vacaciones actuales</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-muted py-4">
                        <i className="fas fa-inbox fa-2x mb-2"></i>
                        <div>No hay historial de vacaciones</div>
                        <small>Este colaborador no tiene registros previos</small>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex-lg-grow-0" style={{ width: "70%" }}>
          <div className="card border-0 rounded mb-4">
            <Panel>
              <PanelHeader noButton={true} className="bg-azul">
                Datos generales de las vacaciones
              </PanelHeader>
              <div className="p-4">
                <ConfigProvider locale={esES}>
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <label className="form-label">Descripción</label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={holiday.description || ""}
                        disabled
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Rango de fechas</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formatDateRange(
                          holiday.startDate,
                          holiday.endDate
                        )}
                        disabled
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Días Solicitados</label>
                      <input
                        type="number"
                        className="form-control"
                        value={holiday.daysRequested || 1}
                        disabled
                      />
                    </div>

                    {!isAlreadyProcessed && (
                      <div className="col-md-12 mb-3">
                        <label className="form-label">Decisión *</label>
                        <div className="mt-2">
                          <div className="form-check form-check-inline">
                            <input
                              type="radio"
                              name="approved"
                              value="true"
                              className="form-check-input"
                              id="approve"
                              checked={formik.values.approved === true}
                              onChange={() =>
                                formik.setFieldValue("approved", true)
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="approve"
                            >
                              Aprobar
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              type="radio"
                              name="approved"
                              value="false"
                              className="form-check-input"
                              id="reject"
                              checked={formik.values.approved === false}
                              onChange={() =>
                                formik.setFieldValue("approved", false)
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="reject"
                            >
                              Rechazar
                            </label>
                          </div>
                        </div>
                        {formik.touched.approved && formik.errors.approved && (
                          <div className="text-danger small mt-1">
                            {formik.errors.approved}
                          </div>
                        )}
                      </div>
                    )}

                    {!isAlreadyProcessed && formik.values.approved === true && (
                      <div className="col-md-12 mb-3">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            id="requireTacticRelease"
                            className="form-check-input"
                            checked={!!formik.values.requireTacticRelease}
                            onChange={(e) =>
                              formik.setFieldValue(
                                "requireTacticRelease",
                                e.target.checked
                              )
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="requireTacticRelease"
                          >
                            ¿Es necesario liberar automáticamente los horarios
                            conflictivos durante el período de vacaciones?
                          </label>
                        </div>
                        {formik.values.requireTacticRelease &&
                          partnerSchedule &&
                          partnerSchedule.length > 0 && (
                            <div className="alert alert-info mt-2 small">
                              <i className="fas fa-info-circle me-2"></i>
                              <strong>
                                Se eliminarán automáticamente{" "}
                                {partnerSchedule.length} horario
                                {partnerSchedule.length > 1 ? "s" : ""}
                              </strong>{" "}
                              del colaborador durante el período de vacaciones
                              aprobado.
                              <br />
                              <small className="text-muted">
                                Esto liberará las posiciones para que puedan ser
                                cubiertas por otros colaboradores durante el
                                período de vacaciones.
                              </small>
                            </div>
                          )}
                      </div>
                    )}

                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        Comentario del revisor{" "}
                        {formik.values.approved === false && (
                          <span className="text-danger">*</span>
                        )}
                      </label>
                      <textarea
                        className={`form-control ${
                          formik.touched.reviewerComment &&
                          formik.errors.reviewerComment
                            ? "is-invalid"
                            : ""
                        }`}
                        rows="3"
                        placeholder={"Comentario del revisor"}
                        value={formik.values.reviewerComment}
                        onChange={(e) =>
                          formik.setFieldValue(
                            "reviewerComment",
                            e.target.value
                          )
                        }
                        onBlur={formik.handleBlur}
                        name="reviewerComment"
                        disabled={isAlreadyProcessed}
                      />
                      {formik.touched.reviewerComment &&
                        formik.errors.reviewerComment && (
                          <div className="invalid-feedback">
                            {formik.errors.reviewerComment}
                          </div>
                        )}
                    </div>
                  </div>
                </ConfigProvider>
              </div>
            </Panel>
          </div>

          <div className="card border-0 rounded">
            <Panel>
              <PanelHeader noButton={true} className="bg-azul">
                Archivos adjuntos de las vacaciones
              </PanelHeader>
              <HolidayFormEditFiles
                holiday={holiday}
                reloadFiles={handleReloadFiles}
                isDisabled={isAlreadyProcessed}
              />
            </Panel>
          </div>
        </div>
      </div>

      <div className="card border-0 m-4 rounded p-3">
        <div className="row justify-content-end">
          <div className="col-md-3">
            <Button
              onClick={() => navigate(-1)}
              title="Cancelar"
              variant="secondary"
              type="button"
              disabled={loading}
            />
          </div>
          {!isAlreadyProcessed ? (
            <div className="col-md-3">
              <Button
                onClick={() => formik.handleSubmit()}
                title="Procesar"
                variant="azul"
                disabled={loading}
                type="submit"
              />
            </div>
          ) : canApproveAsResponsible ? (
            <div className="col-md-3">
              <Button
                onClick={handleApproveAsResponsible}
                title="Aprobar como Responsable"
                variant="success"
                disabled={loading}
                type="button"
              />
            </div>
          ) : (
            <div className="col-md-3">
              <Button
                onClick={() => navigate(-1)}
                title={`Vacaciones ${getHolidayStatus().toLowerCase()}`}
                variant="secondary"
                type="button"
                disabled={true}
              />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ApprovedHoliday;
